import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import {
  TRACK_PATIENT_EDUCATION_HEAP_EVENT,
  TrackPatientEducationHeapEventData,
  TrackPatientEducationHeapEventVariables,
} from "@app/service/patientEducation";
import { HEAP_EVENTS } from "@app/provider/types";

interface TrackEventProps {
  eventKey: HEAP_EVENTS;
  eventProperties?: Record<string, unknown>;
}

interface UsePatientEducationHeapReturn {
  trackEvent: (props: TrackEventProps) => Promise<void>;
}

/**
 * Hook for tracking patient education Heap events
 * Uses a backend proxy to include limsClinicId without exposing it to the frontend
 */
export const usePatientEducationHeap = (
  clinicPublicId: string | null
): UsePatientEducationHeapReturn => {
  const [trackHeapEvent] = useMutation<
    TrackPatientEducationHeapEventData,
    TrackPatientEducationHeapEventVariables
  >(TRACK_PATIENT_EDUCATION_HEAP_EVENT);

  const trackEvent = useCallback(
    async ({ eventKey, eventProperties = {} }: TrackEventProps) => {
      if (!clinicPublicId) {
        console.warn(
          "Cannot track Heap event: clinicPublicId is not available"
        );
        return;
      }

      try {
        await trackHeapEvent({
          variables: {
            input: {
              clinicPublicId,
              eventName: eventKey,
              eventPropertiesJson:
                Object.keys(eventProperties).length > 0
                  ? JSON.stringify(eventProperties)
                  : undefined,
            },
          },
        });
      } catch (error) {
        // Log error but don't throw - Heap tracking failures shouldn't break user flow
        console.error("Failed to track patient education Heap event:", error);
      }
    },
    [clinicPublicId, trackHeapEvent]
  );

  return { trackEvent };
};
